import { useCallback, useRef, useState } from 'react';

import { exportApi } from '../../api/export.js';
import { rulesApi } from '../../api/rules.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { Permission } from '../../auth/roles.js';
import {
  Card, ErrorState, ExportCsvButton, LoadingState, MessageBanner,
} from '../../components/ui';
import { PRICE_LEVELS } from '../../constants/priceLevels.js';
import { useInfiniteScroll } from '../../hooks';
import { useUrlColumns, useUrlDebouncedParam } from '../../url';
import { ColumnPicker } from './ColumnPicker.jsx';
import { HOL_DEFAULT_COLS } from './constants.js';
import { HolOverrideAddForm } from './HolOverrideAddForm.jsx';

const FROZEN_WIDTH = 120;

// ``urlActive`` — this is the visible Rules sub-tab, so its filters own the
// query string. See DataExplorer for why the hidden explorers must not.
export function HolOverrideExplorer({ urlActive = true }) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(Permission.EDIT_RULES);

  const urlOpts = { active: urlActive };
  const [selectedCols, setSelectedCols] = useUrlColumns('cols', HOL_DEFAULT_COLS, urlOpts);
  const [pickerOpen,   setPickerOpen]   = useState(false);
  const [search, setSearch, debouncedSearch] = useUrlDebouncedParam('q', urlOpts);
  const [msg,          setMsg]          = useState(null);
  const [saving,       setSaving]       = useState(null);
  const [adding,       setAdding]       = useState(false);
  const [selected,     setSelected]     = useState(new Set());

  // The table scrolls inside its own box so the header row can stay frozen —
  // that box is also the infinite-scroll sentinel's root.
  const scrollRef = useRef(null);

  const fetchPage = useCallback(
    (offset, limit) => rulesApi.listHolOverrides({ limit, offset, search: debouncedSearch }),
    [debouncedSearch],
  );

  const {
    items, summary, columns: rawColumns, total,
    loading, loadingMore, hasMore, error, sentinelRef, updateItem, removeItems, reset,
  } = useInfiniteScroll(fetchPage, [fetchPage], { rootRef: scrollRef });

  const allColumns = rawColumns.filter((c) => !c.editable);
  const activeCols = allColumns.filter((c) => selectedCols.has(c.key));

  const toggleCol = (key) => {
    const next = new Set(selectedCols);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelectedCols(next);
  };

  const selectTable = (table) => {
    const tableKeys = allColumns.filter((c) => c.table === table).map((c) => c.key);
    const allSelected = tableKeys.every((k) => selectedCols.has(k));
    const next = new Set(selectedCols);
    tableKeys.forEach((k) => (allSelected ? next.delete(k) : next.add(k)));
    setSelectedCols(next);
  };

  const handlePriceLevelChange = async (row, newValue) => {
    setSaving(`${row.id}_override`);
    try {
      await rulesApi.patchHolOverrideField(row.id, 'override_price_level', newValue);
      updateItem('id', row.id, { override_price_level: newValue });
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
    setSaving(null);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => (
      prev.size === items.length && items.length > 0
        ? new Set()
        : new Set(items.map((r) => r.id))
    ));
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} pay code override${selected.size === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setSaving('bulk_delete');
    try {
      const ids = [...selected];
      const res = await rulesApi.bulkDeleteHolOverrides(ids);
      removeItems('id', ids);
      setSelected(new Set());
      setMsg({ type: 'success', text: `Deleted ${res.deleted} override${res.deleted === 1 ? '' : 's'}` });
    } catch (e) {
      setMsg({ type: 'error', text: `Delete failed: ${e.message}` });
    }
    setSaving(null);
  };

  return (
    <>
      {summary && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          <Card label="Total Overrides" value={summary.total} />
          <Card label="Locations"            value={summary.sites}        color="#0d47a1" />
          <Card label="Unique Labor Metrics" value={summary.unique_tasks} color="#e65100" />
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setPickerOpen(!pickerOpen)}
          style={{ marginBottom: 8 }}
        >
          {pickerOpen ? 'Hide Columns' : 'Choose Columns'} ({activeCols.length} selected)
        </button>
        {pickerOpen && (
          <ColumnPicker
            columns={allColumns}
            selectedCols={selectedCols}
            onToggle={toggleCol}
            onSelectTable={selectTable}
            extraEditableNote={
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <strong style={{ fontSize: 12, color: '#333' }}>Editable Columns</strong>
                  <span style={{ fontSize: 10, color: '#999' }}>(always visible, frozen right)</span>
                </div>
                <span style={{ fontSize: 12, color: '#666' }}>Override Price Level (Price Lvl)</span>
              </div>
            }
          />
        )}
      </div>

      <div style={{
        display: 'flex', gap: 12, marginBottom: 16,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Search</label>
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..." style={{ width: 200 }}
          />
        </div>
        {canEdit && (
          <button
            className="btn btn-primary"
            onClick={() => setAdding(true)}
            disabled={adding}
            style={{ alignSelf: 'flex-end' }}
          >
            Add Pay Code Override
          </button>
        )}
        {canEdit && selected.size > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', alignSelf: 'flex-end', paddingBottom: 4 }}>
            <span style={{ fontSize: 13, color: '#555' }}>{selected.size} selected</span>
            <button
              className="btn btn-sm btn-danger"
              onClick={handleBulkDelete}
              disabled={saving === 'bulk_delete'}
            >
              {saving === 'bulk_delete' ? 'Deleting...' : `Delete ${selected.size}`}
            </button>
          </div>
        )}
        <ExportCsvButton
          onExport={() => exportApi.download('hol_overrides', {
            params: { search: debouncedSearch },
            // Rule # + the chosen columns + the frozen Price Level cell.
            columns: ['id', ...activeCols.map((c) => c.key), 'override_price_level'],
          })}
          disabled={loading || total === 0}
          title="Download every override matching this search as CSV — not just the rows loaded here"
          onError={(text) => setMsg({ type: 'error', text })}
          style={{ alignSelf: 'flex-end', paddingBottom: 2 }}
        />
        <div style={{ fontSize: 12, color: '#666', alignSelf: 'flex-end', paddingBottom: 4 }}>
          Showing {items.length} of {total}
        </div>
      </div>

      {adding && (
        <HolOverrideAddForm
          onSave={async (data) => {
            try {
              await rulesApi.createHolOverride(data);
              setMsg({ type: 'success', text: `Created override for ${data.labor_metric_code}` });
              setAdding(false);
              reset();
            } catch (e) {
              setMsg({ type: 'error', text: e.message });
            }
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <MessageBanner msg={msg} onDismiss={() => setMsg(null)} />
      {error && <ErrorState>{error}</ErrorState>}

      {loading
        ? <LoadingState>Loading pay code overrides...</LoadingState>
        : (
          <div className="table-scroll" ref={scrollRef}>
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center' }}>
                    {canEdit && (
                      <input
                        type="checkbox" className="cb"
                        onChange={toggleSelectAll}
                        checked={selected.size === items.length && items.length > 0}
                      />
                    )}
                  </th>
                  <th>Rule #</th>
                  {activeCols.map((col) => (
                    <th key={col.key} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'block', fontSize: 9, fontWeight: 400, color: '#999' }}>
                        {col.table || col.group}
                      </span>
                      {col.label}
                    </th>
                  ))}
                  <th style={{
                    fontSize: 11, whiteSpace: 'nowrap',
                    width: FROZEN_WIDTH, minWidth: FROZEN_WIDTH, maxWidth: FROZEN_WIDTH,
                    // z-4: above the frozen header row (z-3) it slides under.
                    position: 'sticky', right: 0, zIndex: 4,
                    background: '#002159', boxShadow: '-2px 0 4px rgba(0,0,0,0.15)',
                  }}>
                    <span style={{ display: 'block', fontSize: 9, fontWeight: 400, color: '#90caf9' }}>Rule</span>
                    Price Lvl
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id}>
                    <td style={{ textAlign: 'center' }}>
                      {canEdit && (
                        <input
                          type="checkbox" className="cb"
                          checked={selected.has(r.id)}
                          onChange={() => toggleSelect(r.id)}
                        />
                      )}
                    </td>
                    <td><strong>{r.id}</strong></td>
                    {activeCols.map((col) => (
                      <td key={col.key} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {r[col.key] || <span style={{ color: '#ccc' }}>—</span>}
                      </td>
                    ))}
                    <td style={{
                      textAlign: 'center', padding: '4px 6px',
                      width: FROZEN_WIDTH, minWidth: FROZEN_WIDTH, maxWidth: FROZEN_WIDTH,
                      position: 'sticky', right: 0, zIndex: 1,
                      background: '#fff', boxShadow: '-2px 0 4px rgba(0,0,0,0.06)',
                    }}>
                      {canEdit ? (
                        <select
                          value={r.override_price_level || ''}
                          onChange={(e) => handlePriceLevelChange(r, e.target.value)}
                          disabled={saving === `${r.id}_override`}
                          style={{
                            fontSize: 11, padding: '2px 2px', width: '100%',
                            maxWidth: 108, borderRadius: 4, border: '1px solid #ccc',
                          }}
                        >
                          <option value="">—</option>
                          {PRICE_LEVELS.map((pl) => <option key={pl} value={pl}>{pl}</option>)}
                        </select>
                      ) : (
                        <span style={{ fontSize: 11, color: '#666' }}>
                          {r.override_price_level || '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: 12, color: '#666', fontSize: 13 }}>
                Loading more...
              </div>
            )}
            {!hasMore && items.length > 0 && (
              <div style={{ textAlign: 'center', padding: 12, color: '#999', fontSize: 12 }}>
                All {total} overrides loaded
              </div>
            )}
          </div>
        )
      }
    </>
  );
}
